import type { SceneConfig } from '../sceneConfig';
import type { ColliderDef } from '../collision/colliderTypes';
import { resolveCdnUrl } from '../../utils/cdnUrl';

const KAABA_INSIDE_2M_URL = resolveCdnUrl('models/inside/kaaba/Kaaba_new_wall_2000K.sog');
const KAABA_INSIDE_1M_URL = resolveCdnUrl('models/inside/kaaba/Kaaba_new_wall_1000K.sog');
const KAABA_INSIDE_600K_URL = resolveCdnUrl('models/inside/kaaba/Kaaba_new_wall_600K.sog');

const PREVIEW_ORBIT_CENTER = { x: -0.03, y: 1.85, z: -0.04 };
const MAIN_ORBIT_CENTER = { x: 0.14, y: 0.88, z: -1.45 };
const WAVES_CENTER = { x: 0, y: 0, z: 0 };
const REVEAL_CENTER = MAIN_ORBIT_CENTER
/**
 * Колайдеры закрытой комнаты. Одна hollow box — стены/потолок/пол.
 * Габариты взяты из splat-meta `means.mins/maxs` файла Kaaba_new_wall_600K.sog:
 *   mins ≈ (-2.12, -1.60, -2.20)
 *   maxs ≈ ( 1.84,  2.77,  2.18)
 * Это полный extent точек splat'а — стены прорисованы примерно по этим
 * границам. TUNE: если камера упирается в стену "раньше" чем хочется
 * (камера cameraRadius padding 0.05 + PUSH_EPSILON) или, наоборот,
 * проваливается чуть внутрь стены — подкрути size/position здесь.
 */
const KAABA_INSIDE_COLLIDERS: ColliderDef[] = [
  {
    id: 'kaaba-inside-room',
    type: 'box',
    mode: 'hollow',
    enabled: true,
    blocksLineOfSight: false,
    position: { x: -0.14, y: 0.585, z: -0.402 },
    size: { x: 7.7, y: 10.5, z: 9 },
  },
];

export const kaabaInsideSceneConfig: SceneConfig = {
  id: 'kaaba-inside',
  // Скрываем "Journey to {scene}" subtitle на прелоадере — для интерьерной
  // сцены имя «Kaaba inside» в подзаголовке избыточно, достаточно одного
  // "Pilgrim" в заголовке.
  disableSubtitle: true,
  renderer: {
    clearColor: 0x0f0f13,
  },
  camera: {
    // TUNE: interior sits in a small bounded volume; near can be tiny.
    fov: 100,
    near: 0.01,
    far: 100,
    // TUNE: starting camera pose — pick somewhere inside the model.
    initialPosition: { x: -1.56, y: 3.77, z: -0.07 },
    // TUNE: orbit-style preview tour around the model before reveal.
    previewTourStart: { x: -6.05, y: 2.42, z: -0.08 },
    previewTourEnd: {
      position: { x: -1.86, y: 0.25, z: -3.99 },
      target: MAIN_ORBIT_CENTER,
    },
    previewTourEnterDuration: 1.5,
  },
  controls: {
    orbitMinDistance: 0,
    orbitMaxDistance: 10,
    // TUNE: на основе диапазона y подобранных поз (~1.96..3.78). Сузить,
    // когда добавятся POI walk-up'ы.
    clampMinHeight: 0,
    clampMaxHeight: Infinity,
    clampMaxHeightFar: Infinity,
    // Drag-to-look only (как в Mina).
    forcedViewMode: 'first-person',
    constrainVertical: true,
    verticalMin: Math.PI / 6,        // 30°
    verticalMax: (5 * Math.PI) / 6,  // 150°
    lookSpeedHorizontal: 120,
    lookSpeedVertical: 80,
    lookSpeedHorizontalMobile: 60,
    lookSpeedVerticalMobile: 40,
    orbitCenter: MAIN_ORBIT_CENTER,
  },
  colliders: KAABA_INSIDE_COLLIDERS,
  poi: {
    clusterEnabled: false,
  },
  splat: {
    defaultByQuality: {
      desktop: 'kaaba_inside_2m',
      mobile: {
        high: 'kaaba_inside_1m',
        middle: 'kaaba_inside_1m',
        low: 'kaaba_inside_600k',
      },
    },
    variants: [
      { key: 'kaaba_inside_2m', url: KAABA_INSIDE_2M_URL },
      { key: 'kaaba_inside_1m', url: KAABA_INSIDE_1M_URL },
      { key: 'kaaba_inside_600k', url: KAABA_INSIDE_600K_URL },
    ],
    revealAnimationDuration: 3.0,
    revealDistanceScale: 2.0,
    revealMode: '3d',
    // TUNE: reveal origin — usually the visual centre of the model.
    revealOrigin: REVEAL_CENTER,
  },
  previewModel: {
    url: resolveCdnUrl('preview/Kaaba_preloader_v2_pnts_150000_meshopt(16).glb'),
    orbitCenter: PREVIEW_ORBIT_CENTER,
    style: {
      pointSizeFactor: 2.5,
      appearanceDuration: 1.5,
      wavesCenter: WAVES_CENTER,
      wavesAmplitude: 2.0,
      wavesPeriod: 1.0,
      wavesSpeed: 1.5,
      wavesColorIntensity: 6.0,
      wavesDisplacement: 0.02,
    },
  },
  assistant: {
    // Reuse kaaba/mina teller placement — interior is similarly small-scale.
    position: { x: 0, y: 0, z: -0.03 },
    rotationY: 0,
    scale: 0.0000625,
    reflectionResolution: 128,
  },
  environment: {
    // Warm interior lighting; no skybox (no sky visible inside).
    hemisphereLight: {
      skyColor: 0xffe6c4,
      groundColor: 0x1a1410,
      intensity: 0.4,
    },
    directionalLights: [
      {
        color: 0xfff2d5,
        intensity: 0.7,
        position: { x: 0, y: 1, z: 0.3 },
      },
    ],
    background: {
      color: 0x0f0f13,
    },
  },
  virtualTour: [],
};
